/** Presentation helpers for the API Requests grid + detail dialog. */
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

/** API_Request.TYPES labels, 0-based. Contract-tested against the spec's
 *  `x-type-labels`. */
export const API_REQUEST_TYPE_LABELS = [
  'Unknown',
  'Booking webhook',
  'Departure webhook',
  'Product webhook',
  'Twilio webhook',
  'Order webhook',
  'Notification',
];

/** API_Request.STORE_OPTIONS labels, 0-based. Contract-tested against the
 *  spec's `x-store-labels`. */
export const API_REQUEST_STORE_LABELS = [
  'Store for 3 days',
  'Store until expires',
  'Store forever',
];

export function apiRequestTypeLabel(type: number | null): string {
  if (type == null) return '—';
  return API_REQUEST_TYPE_LABELS[type] ?? `Type ${type}`;
}

export function apiRequestStoreLabel(store: number | null): string {
  if (store == null) return '—';
  return API_REQUEST_STORE_LABELS[store] ?? `Store ${store}`;
}

function statusColor(code: number | null): ChipProps['color'] {
  if (!code) return 'default';
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  if (code >= 200 && code < 300) return 'success';
  return 'info';
}

/** Response HTTP status as a colored chip (0 / null → "—"). */
export function StatusCodeChip({ code }: { code: number | null }) {
  const color = statusColor(code);
  return (
    <Chip
      size="small"
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      label={code ? String(code) : '—'}
      sx={{ height: 22, fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
    />
  );
}

/** Compact humanized duration from milliseconds. */
export function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
